/**
 * useConnectionCoordinator Hook
 *
 * Coordinates connection health status from the currently active softphone (legacy or new).
 * Automatically detects which softphone is active and returns appropriate connection state.
 *
 * This hook acts as a coordinator, routing to the appropriate softphone implementation
 * without maintaining its own state. It provides a unified interface for connection health
 * monitoring across both legacy and new softphone architectures.
 *
 * @example
 * ```tsx
 * const { isWebRTCConnected, isWebRTCReconnecting, webRTCRegistrationConcluded } =
 *   useConnectionCoordinator();
 * ```
 */

import type { SipRegistrationStatus } from './index.types';
import { MobXProviderContext } from 'mobx-react';
import {
  useSoftphoneCoordinatorStore,
  useSoftphoneStore,
} from 'Modules/softphone/store';
import type { SipStatus } from 'Modules/softphone/types';
import { useContext } from 'react';
import type { RootStoreProps } from 'Stores/RootStore.types';

/**
 * Maps new softphone SipStatus to legacy SIP registration status interface
 *
 * This mapping is necessary to ensure correct functionality during coordination between
 * legacy and new softphone implementations. Legacy components expect the SipRegistrationStatus
 * interface with three boolean flags (isWebRTCConnected, isWebRTCReconnecting,
 * webRTCRegistrationConcluded), while the new softphone uses a more semantic SipStatus enum.
 *
 * @todo After legacy softphone removal, we can use SipStatus directly
 * instead of the boolean flags approach. This would eliminate the need for this mapping and
 * provide better state management through mutually exclusive states.
 *
 * @param status - The SipStatus from new softphone
 * @returns SipRegistrationStatus compatible with legacy interface
 */
function mapSipStatusToLegacyFormat(status: SipStatus): SipRegistrationStatus {
  switch (status) {
    case 'registered':
      // Fully registered and ready for calls
      return {
        isWebRTCConnected: true,
        isWebRTCReconnecting: false,
        webRTCRegistrationConcluded: true,
      };

    case 'connecting':
    case 'registering':
      // In process of connecting/registering
      return {
        isWebRTCConnected: false,
        isWebRTCReconnecting: true,
        webRTCRegistrationConcluded: false,
      };

    case 'connected':
      // Transport connected but not yet registered
      return {
        isWebRTCConnected: true,
        isWebRTCReconnecting: false,
        webRTCRegistrationConcluded: false,
      };

    case 'disconnected':
    case 'failed':
    case 'unregistering':
    default:
      // Not connected or in error state
      return {
        isWebRTCConnected: false,
        isWebRTCReconnecting: false,
        webRTCRegistrationConcluded: false,
      };
  }
}

/**
 * Hook that coordinates SIP registration status from the active softphone
 *
 * Detects which softphone (legacy or new) is currently active and returns
 * the appropriate SIP registration status. The returned interface is compatible
 * with legacy phoneCall store for seamless integration.
 *
 * @returns SipRegistrationStatus object with registration state flags
 */
export function useConnectionCoordinator(): SipRegistrationStatus {
  // Get person ID for coordinator store
  const rootStore = useContext<RootStoreProps>(MobXProviderContext);
  const { person, phoneCall } = rootStore;
  const personId = person?.loggedInPersonId;

  // Check which softphone is active
  const coordinatorStore = useSoftphoneCoordinatorStore(personId);
  const isNewSoftphoneEnabled = coordinatorStore(
    (state) => state.isNewSoftphoneEnabled
  );

  // Get new softphone state
  const sipStatus = useSoftphoneStore((state) => state.status);

  // Handle missing personId gracefully
  if (!personId) {
    return {
      isWebRTCConnected: false,
      isWebRTCReconnecting: false,
      webRTCRegistrationConcluded: false,
    };
  }

  if (isNewSoftphoneEnabled) {
    // Map new softphone SipStatus to legacy interface
    return mapSipStatusToLegacyFormat(sipStatus);
  } else {
    // Return legacy softphone status directly
    // Note: phoneCall is a MobX observable, so this will be reactive
    // when used in an observer component
    return {
      isWebRTCConnected: phoneCall.isWebRTCConnected,
      isWebRTCReconnecting: phoneCall.isWebRTCReconnecting,
      webRTCRegistrationConcluded: phoneCall.webRTCRegistrationConcluded,
    };
  }
}

